import type { Lang } from "../index";

export interface FreelancerDiscoveryCallCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: {
    heading: string;
    body: string;
    bullets: string[];
  };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: FreelancerDiscoveryCallCopy = {
  seo: {
    title: "Minuta for freelancers — live English copilot for discovery calls",
    description:
      "Freelancers and technical consultants use Minuta to answer pricing, scope, timeline, risk, and credibility questions in English during the call — not just after it.",
  },
  eyebrow: "Use case · Freelancer",
  h1: "Answer the hard client question while you are still on the call.",
  lede:
    "The deal is won or lost when the client asks about day rate, scope creep, timeline, risk, or why they should trust you. Minuta is the live English copilot that reads the question, uses the context you prepared, and drafts the answer before the moment passes.",
  problem: {
    heading: "You know the work. The high-stakes English is the bottleneck.",
    body1:
      "A US client asks, \"Can you do this for less?\", \"Can we include that extra integration?\", \"Can you ship it before launch?\", or \"Why should we hire you instead of an agency?\" You have the answer in your head. Under pressure, in English, it comes out smaller, slower, and less senior than your real judgment.",
    body2:
      "Generic notetakers help after the meeting. This problem happens during the meeting. Minuta sits beside the call, listens locally, and turns your rate sheet, scope rules, case studies, technical notes, and delivery plan into language you can say while the client is still there.",
  },
  scenarios: {
    heading: "The moments Minuta is built for",
    items: [
      {
        tag: "Day rate",
        title: "\"What's your day rate?\"",
        body: "Before the call, you load your rate sheet, minimum engagement, and preferred fixed-bid language. Minuta suggests: state the rate, tie it to senior ownership, and offer a scoped alternative without discounting. Result: you anchor price clearly instead of apologizing for it.",
      },
      {
        tag: "Scope creep",
        title: "\"Can you also include this?\"",
        body: "You preloaded your scope template, change-request policy, and examples of what belongs in phase two. Minuta suggests: acknowledge the goal, separate it from the current scope, and price it as an option. Result: you stay helpful without donating margin.",
      },
      {
        tag: "Timeline",
        title: "\"Can you ship before our launch?\"",
        body: "You attach your delivery plan, dependency checklist, and known risk list. Minuta suggests: name the feasible milestone, call out the blockers, and propose a cut line for the launch version. Result: the client hears confidence and constraints, not a nervous yes.",
      },
      {
        tag: "Credibility",
        title: "\"Why should we hire you?\"",
        body: "You load the case studies, metrics, and project notes you want to reuse. Minuta suggests a concise answer with the closest proof point and a specific outcome you have shipped. Result: you sound like a specialist with receipts, not a freelancer making generic claims.",
      },
      {
        tag: "Tradeoffs",
        title: "\"Why that technical approach?\"",
        body: "You add architecture notes, stack preferences, and tradeoffs from past projects. Minuta suggests: explain the option, the risk it avoids, and where you would choose differently. Result: the call moves from language pressure to senior technical judgment.",
      },
    ],
  },
  how: {
    heading: "How Minuta works before and during the call",
    body:
      "Open Minuta before Zoom, Teams, or Meet. Attach the materials you already use to sell: pricing, proposal notes, scope template, case studies, delivery plan, and technical tradeoff notes. Then run the call normally.",
    bullets: [
      "No bot joins. The client sees a normal call while Minuta runs on your computer.",
      "Live English transcript on the side, so accents, speed, and nerves do not erase the exact question.",
      "Hard question appears → copilot draft in seconds → answer grounded in your own pricing, scope, proof, risk, and technical context.",
      "After the call, you still get structured notes and follow-up material. The wedge is that you already answered better while the client was listening.",
    ],
  },
  proof: {
    heading: "Why this is not another notetaker",
    bullets: [
      "Transcripts help you remember what happened. Minuta helps you answer while the outcome is still being decided.",
      "Every suggestion is grounded in docs you loaded before the call: rate sheet, scope boundaries, case studies, risk notes, and delivery plan.",
      "Invisible by design. No guest bot announces that you need help on a high-trust sales call.",
      "Zero-retention processing keeps confidential client scope and pricing conversations on your computer.",
    ],
  },
  cta: {
    heading: "Try it before your next discovery call",
    body: "7 days with everything unlocked. No credit card. €9/mo after.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: FreelancerDiscoveryCallCopy = {
  seo: {
    title: "Minuta para freelancers — copiloto ao vivo para discovery calls em inglês",
    description:
      "Freelancers e consultores técnicos usam o Minuta para responder preço, escopo, prazo, risco e credibilidade em inglês durante a call — não só depois.",
  },
  eyebrow: "Caso de uso · Freelancer",
  h1: "Responda a pergunta difícil do cliente enquanto você ainda está na call.",
  lede:
    "O deal é ganho ou perdido quando o cliente pergunta sobre day rate, scope creep, prazo, risco ou por que deveria confiar em você. O Minuta é o copiloto ao vivo em inglês que lê a pergunta, usa o contexto que você preparou e monta a resposta antes do momento passar.",
  problem: {
    heading: "Você sabe fazer o trabalho. O inglês sob pressão é o gargalo.",
    body1:
      "Um cliente US pergunta: \"Can you do this for less?\", \"Can we include that extra integration?\", \"Can you ship it before launch?\" ou \"Why should we hire you instead of an agency?\" Você tem a resposta na cabeça. Sob pressão, em inglês, ela sai menor, mais lenta e menos sênior do que seu julgamento real.",
    body2:
      "Notetaker genérico ajuda depois da reunião. Esse problema acontece durante a reunião. O Minuta fica ao lado da call, escuta localmente e transforma rate sheet, regras de escopo, case studies, notas técnicas e plano de entrega em linguagem que você consegue falar enquanto o cliente ainda está ali.",
  },
  scenarios: {
    heading: "Os momentos para os quais o Minuta foi feito",
    items: [
      {
        tag: "Day rate",
        title: "\"What's your day rate?\"",
        body: "Antes da call, você carrega sua tabela de preço, mínimo de projeto e linguagem preferida para fixed bid. O Minuta sugere: diga o rate, conecte com ownership sênior e ofereça uma alternativa escopada sem dar desconto. Resultado: você ancora preço com clareza em vez de pedir desculpa por ele.",
      },
      {
        tag: "Scope creep",
        title: "\"Can you also include this?\"",
        body: "Você pré-carrega seu template de escopo, política de change request e exemplos do que entra na fase dois. O Minuta sugere: reconheça o objetivo, separe do escopo atual e coloque como opção com preço. Resultado: você continua prestativo sem doar margem.",
      },
      {
        tag: "Prazo",
        title: "\"Can you ship before our launch?\"",
        body: "Você anexa plano de entrega, checklist de dependências e lista de riscos conhecidos. O Minuta sugere: nomeie o marco viável, exponha os bloqueios e proponha uma linha de corte para a versão de lançamento. Resultado: o cliente ouve confiança com restrições, não um sim nervoso.",
      },
      {
        tag: "Credibilidade",
        title: "\"Why should we hire you?\"",
        body: "Você carrega case studies, métricas e notas de projetos que quer reutilizar. O Minuta sugere uma resposta curta com a prova mais próxima e um resultado específico que você já entregou. Resultado: você soa como especialista com evidência, não como freelancer fazendo claim genérico.",
      },
      {
        tag: "Tradeoffs",
        title: "\"Why that technical approach?\"",
        body: "Você adiciona notas de arquitetura, preferências de stack e tradeoffs de projetos passados. O Minuta sugere: explique a opção, o risco que ela evita e em que cenário você escolheria diferente. Resultado: a call sai da pressão do idioma e volta para julgamento técnico sênior.",
      },
    ],
  },
  how: {
    heading: "Como o Minuta funciona antes e durante a call",
    body:
      "Abra o Minuta antes do Zoom, Teams ou Meet. Anexe os materiais que você já usa para vender: pricing, notas de proposta, template de escopo, case studies, plano de entrega e notas de tradeoff técnico. Depois rode a call normalmente.",
    bullets: [
      "Nenhum bot entra. O cliente vê uma call normal enquanto o Minuta roda no seu computador.",
      "Transcript em inglês ao vivo do lado, para sotaque, velocidade e nervosismo não apagarem a pergunta exata.",
      "Pergunta difícil aparece → rascunho do copiloto em segundos → resposta baseada no seu preço, escopo, prova, risco e contexto técnico.",
      "Depois da call, você ainda recebe notas estruturadas e material de follow-up. A diferença é que você já respondeu melhor enquanto o cliente estava ouvindo.",
    ],
  },
  proof: {
    heading: "Por que isso não é mais um notetaker",
    bullets: [
      "Transcripts ajudam você a lembrar o que aconteceu. O Minuta ajuda você a responder enquanto o resultado ainda está sendo decidido.",
      "Toda sugestão é fundamentada nos docs que você carregou antes da call: preço, limites de escopo, case studies, notas de risco e plano de entrega.",
      "Invisível por design. Nenhum bot convidado anuncia que você precisa de ajuda numa call comercial de alta confiança.",
      "Processamento com retenção zero mantém conversas confidenciais de escopo e preço no seu computador.",
    ],
  },
  cta: {
    heading: "Testa antes da sua próxima discovery",
    body: "7 dias com tudo desbloqueado. Sem cartão. €9/mês depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: FreelancerDiscoveryCallCopy = {
  seo: {
    title: "Minuta para freelancers — copiloto en vivo para discovery calls en inglés",
    description:
      "Freelancers y consultores técnicos usan Minuta para responder precio, scope, timeline, riesgo y credibilidad en inglés durante la call — no solo después.",
  },
  eyebrow: "Caso de uso · Freelancer",
  h1: "Responde la pregunta difícil del cliente mientras sigues en la call.",
  lede:
    "El deal se gana o se pierde cuando el cliente pregunta por day rate, scope creep, timeline, riesgo o por qué debería confiar en ti. Minuta es el copiloto en vivo en inglés que lee la pregunta, usa el contexto que preparaste y arma la respuesta antes de que pase el momento.",
  problem: {
    heading: "Sabes hacer el trabajo. El inglés bajo presión es el cuello de botella.",
    body1:
      "Un cliente US pregunta: \"Can you do this for less?\", \"Can we include that extra integration?\", \"Can you ship it before launch?\" o \"Why should we hire you instead of an agency?\" Tienes la respuesta en la cabeza. Bajo presión, en inglés, sale más pequeña, más lenta y menos senior que tu criterio real.",
    body2:
      "Un notetaker genérico ayuda después de la reunión. Este problema ocurre durante la reunión. Minuta se queda al lado de la call, escucha localmente y convierte tu rate sheet, reglas de scope, case studies, notas técnicas y plan de entrega en lenguaje que puedes decir mientras el cliente sigue ahí.",
  },
  scenarios: {
    heading: "Los momentos para los que Minuta está hecho",
    items: [
      {
        tag: "Day rate",
        title: "\"What's your day rate?\"",
        body: "Antes de la call, cargas tu tabla de precios, mínimo de proyecto y lenguaje preferido para fixed bid. Minuta sugiere: di el rate, conéctalo con ownership senior y ofrece una alternativa con scope cerrado sin descontar. Resultado: anclas el precio con claridad en vez de pedir perdón por él.",
      },
      {
        tag: "Scope creep",
        title: "\"Can you also include this?\"",
        body: "Precargas tu template de scope, política de change request y ejemplos de lo que va a fase dos. Minuta sugiere: reconoce el objetivo, sepáralo del scope actual y ponlo como opción con precio. Resultado: sigues siendo útil sin regalar margen.",
      },
      {
        tag: "Timeline",
        title: "\"Can you ship before our launch?\"",
        body: "Adjuntas tu plan de entrega, checklist de dependencias y lista de riesgos conocidos. Minuta sugiere: nombra el milestone viable, muestra los blockers y propone una línea de corte para la versión de launch. Resultado: el cliente escucha confianza con restricciones, no un sí nervioso.",
      },
      {
        tag: "Credibilidad",
        title: "\"Why should we hire you?\"",
        body: "Cargas los case studies, métricas y notas de proyectos que quieres reutilizar. Minuta sugiere una respuesta concisa con la prueba más cercana y un resultado específico que ya entregaste. Resultado: suenas como especialista con evidencia, no como freelancer haciendo claims genéricos.",
      },
      {
        tag: "Tradeoffs",
        title: "\"Why that technical approach?\"",
        body: "Agregas notas de arquitectura, preferencias de stack y tradeoffs de proyectos anteriores. Minuta sugiere: explica la opción, el riesgo que evita y cuándo elegirías otra cosa. Resultado: la call vuelve del estrés del idioma al criterio técnico senior.",
      },
    ],
  },
  how: {
    heading: "Cómo funciona Minuta antes y durante la call",
    body:
      "Abre Minuta antes de Zoom, Teams o Meet. Adjunta los materiales que ya usas para vender: pricing, notas de propuesta, template de scope, case studies, plan de entrega y notas de tradeoffs técnicos. Luego corres la call normalmente.",
    bullets: [
      "Ningún bot entra. El cliente ve una call normal mientras Minuta corre en tu computadora.",
      "Transcript en inglés en vivo al lado, para que acento, velocidad y nervios no borren la pregunta exacta.",
      "Pregunta difícil aparece → borrador del copiloto en segundos → respuesta basada en tu precio, scope, prueba, riesgo y contexto técnico.",
      "Después de la call, igual recibes notas estructuradas y material de follow-up. La diferencia es que ya respondiste mejor mientras el cliente escuchaba.",
    ],
  },
  proof: {
    heading: "Por qué esto no es otro notetaker",
    bullets: [
      "Los transcripts te ayudan a recordar lo que pasó. Minuta te ayuda a responder mientras el resultado todavía se está decidiendo.",
      "Cada sugerencia se basa en los docs que cargaste antes de la call: precio, límites de scope, case studies, notas de riesgo y plan de entrega.",
      "Invisible por diseño. Ningún bot invitado anuncia que necesitas ayuda en una call comercial de alta confianza.",
      "El procesamiento con retención cero mantiene conversaciones confidenciales de scope y precio en tu computadora.",
    ],
  },
  cta: {
    heading: "Pruébalo antes de tu próxima discovery call",
    body: "7 días con todo desbloqueado. Sin tarjeta. €9/mes después.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const freelancerDiscoveryCallCopy: Record<Lang, FreelancerDiscoveryCallCopy> = { en, pt, es };
